import type { GTMPush } from '#commerce/gtm'
import type { GTMEvent } from '#types/gtm'
import { events } from '#commerce/gtm'
import { partytownSnippet } from '@qwik.dev/partytown/integration'
import { stringifyQuery } from 'ufo'

declare global {
  interface Window {
    dataLayer: GTMEvent[]
  }
}

export interface GtmPlugin {
  flushQueue: () => void
  push: GTMPush
  pushEventObj: (eventObj: GTMEvent) => void
  disableAppEvents?: string[]
  queueEvent: GTMPush
}

/**
 * Priority order for GTM events:
 * 1. loadPageData
 * 2. loadUserData
 * 3. routerChangeg
 * 4. All other events (any order)
 */
const PRIORITY_EVENTS = [
  'loadPageData',
  'loadUserData',
  'routerChange'
] as const

declare module '#app' {
  interface NuxtApp {
    $gtm: GtmPlugin
  }
}

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $gtm: GtmPlugin
  }
}

function loadGTMScript(id: string, innerHTML: string, partytown: boolean) {
  if (partytown) {
    const el = document.querySelector(`script[id="${id}"]`)

    if (!el) {
      const script = document.createElement('script')
      script.setAttribute('id', id)
      script.type = 'text/partytown'

      // We add a dataLayer push proxy, so we can emit events into the main thread
      script.innerHTML = `
        window.dataLayer = new Proxy([], {
          set: (obj, prop, value) => {
            if (prop !== 'length') {
              setTimeout(() => {
                // adds timeout so gtm can take time to process and adds an gtm.uniqueId
                window.postMessage({ type: 'gtm', item: value });
              }, 500);
            }
            return Reflect.set(obj, prop, value);
          }
        });
        ${innerHTML}
      `
      document.head.appendChild(script)

      /**
       * We need to add the script in an imperative way because it's required to dispatch this event in order to partytown to load it.
       * By using useHead it doesn't always work the way it should work, sometimes it loads and sometime it doesn't because useHead is async
       */
      window.dispatchEvent(new CustomEvent('ptupdate'))
    }

    // Using this method we expose the gtm layer into the main thread
    window.onmessage = (ev) => {
      if (ev.data.type === 'gtm') {
        // I can't call push because it's a proxy forwarder for partytown
        const partytownPush = window.dataLayer.push
        window.dataLayer[window.dataLayer.length] = ev.data.item
        window.dataLayer.push = partytownPush
      }
    }
    return
  }

  useHead({
    script: [
      {
        id,
        innerHTML,
        type: 'text/javascript'
      }
    ]
  }, { mode: 'client' })
}

export default defineNuxtPlugin<{ gtm: GtmPlugin }>({
  name: 'gtm',
  setup({ hook }) {
    const { features: { gtm }, brand, region } = useRuntimeConfig().public

    const {
      configGtm,
      allowEdgeWorkerExperiments,
      enableFirstPartyGTMScripts
    } = useFeatureFlags()
    const router = useRouter()

    const eventQueue: GTMEvent[] = []
    const pageReady = ref(false)

    // Sorts events according to priority order
    const sortEventsByPriority = (events: GTMEvent[]): GTMEvent[] =>
      events.sort((a, b) => {
        const aKey = (a as any).__sourceEvent ?? a.event
        const bKey = (b as any).__sourceEvent ?? b.event

        const aIndex = PRIORITY_EVENTS.indexOf(aKey as (typeof PRIORITY_EVENTS)[number])
        const bIndex = PRIORITY_EVENTS.indexOf(bKey as (typeof PRIORITY_EVENTS)[number])

        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
        if (aIndex !== -1) return -1
        if (bIndex !== -1) return 1
        return 0
      })

    if (!gtm.enabled) {
      return {
        provide: {
          gtm: {
            flushQueue: () => null,
            push: () => null,
            pushEventObj: () => null,
            queueEvent: () => null
          }
        }
      }
    }

    if (!configGtm) {
      const notConfig = (args) => console.log('%cGTM NOT CONFIGURED', 'background: coral;border-radius: 0.5em;color: white;font-weight: bold;padding: 2px 0.5em;', args)
      return {
        provide: {
          gtm: {
            flushQueue: () => notConfig(null),
            push: (...args) => notConfig(args),
            pushEventObj: notConfig,
            queueEvent: notConfig
          }
        }
      }
    }

    const partytownActive = configGtm.partytown || window.location.search.includes('partytown_debug')

    const log = (...args: any[]) => {
      if (configGtm.debug)
        console.log('%cGTM', 'background: #2E495E;border-radius: 0.5em;color: white;font-weight: bold;padding: 2px 0.5em;', ...args)
    }

    const pushEventObj: GtmPlugin['pushEventObj'] = (eventObj) => {
      if (!window.dataLayer)
        window.dataLayer = []

      window.dataLayer.push(eventObj)
      log('push', eventObj)
    }

    const push: GtmPlugin['push'] = (eventOrArray, ...payload) => {
      const gtmEvents = isArray(eventOrArray) ? eventOrArray : [{ event: eventOrArray, payload }]

      gtmEvents.forEach(({ event, payload: p = payload }) => {
        const [namespace, generator] = event.split('.')
        const eventObj = events[namespace]?.[generator]?.(...p)
        if (eventObj) {
          pushEventObj(eventObj)
        }
      })
    }

    // Flushes all queued events to GTM in priority order
    const flushQueue = () => {
      pageReady.value = true
      const sortedEvents = sortEventsByPriority(eventQueue)

      for (const ev of sortedEvents) {
        pushEventObj(ev)
      }

      // Clear the queue
      eventQueue.length = 0
    }

    /**
     * Queues an event to be pushed when flushQueue() is called
     * flushQueue() must be called manually after all queueEvent() calls are made
     * Events are pushed in priority order: page.onLoadPageData, user.onLoadUserData, page.onRouterChange, then others
     */
    const queueEvent: GtmPlugin['queueEvent'] = (eventOrArray, ...payload) => {
      const gtmEvents = isArray(eventOrArray) ? eventOrArray : [{ event: eventOrArray, payload }]

      gtmEvents.forEach(({ event, payload: p = payload }) => {
        const [namespace, generator] = event.split('.')
        const eventObj = events[namespace]?.[generator]?.(...p)
        if (eventObj) {
          // If flushQueue() has already run, push immediately, otherwise queue
          if (pageReady.value)
            pushEventObj(eventObj)
          else
            eventQueue.push(eventObj)
        }
      })
    }

    router.beforeEach((_to, _from, next) => {
      pageReady.value = false
      next()
    })

    const params = {
      gtm_auth: configGtm.gtm_auth || undefined,
      gtm_preview: configGtm.gtm_preview || undefined,
      gtm_cookies_win: configGtm.gtm_cookies_win || undefined
    }

    const stringifiedParams = stringifyQuery(params)

    const isUsingTagManagerAssistant = window?.location?.search.includes('gtm_debug')

    // use first-party proxy or Google's default
    // const gtmSource = enableFirstPartyGTMScripts ? `/sstm` : 'https://www.googletagmanager.com/gtm.js'
    const gtmSource = enableFirstPartyGTMScripts ? `https://sstm-${region === 'emea' ? 'eu' : 'na'}.${brand}.com/gtm.js` : 'https://www.googletagmanager.com/gtm.js'

    const gtmScript = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  '${gtmSource}?id='+i+dl+ '${stringifiedParams.length > 0 ? `&${stringifiedParams}` : ''}';f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer', '${configGtm.id}');
  `

    const usePartytown = !isUsingTagManagerAssistant && partytownActive

    // partytown config
    if (usePartytown) {
      const currentLocation = window.location.host

      useHead({
        script: [
          {
            id: 'config',
            // function calls on the main thread to forward to web worker

            innerHTML: `partytown =
          { forward: ['dataLayer.push'],
          debug: ${window.location.search.includes('partytown_debug')},
          resolveUrl: (url, location, type) => {
            if (type === 'script' && url.href.indexOf('${currentLocation}') < 0) {
              return '/fapi/proxyScript?url=' + encodeURIComponent(url.href);
            }
            return url;
          },
          };`,
            type: 'text/javascript'
          },
          {
            id: 'partytown',
            innerHTML: partytownSnippet(),
            type: 'text/javascript'
          }
        ]
      })
    }

    const thirdPartyScripts = [
      configGtm.appmeasurement_id ? loadScript(`https://static.${brand}.com/an/production/${configGtm.appmeasurement_id}/appMeasurement.js`) : null
    ]

    /**
     * @description
     * https://digital.vfc.com/jira/browse/GLOBAL15-52262
     * OneTrust script doesn't trigger onload and onerror events using vue/usehead when developer console tab is open, blocking gtm
     */
    Promise.all(thirdPartyScripts)
      .finally(() => loadGTMScript('gtmscript', gtmScript, usePartytown))

    hook('app:mounted', async () => {
      if (allowEdgeWorkerExperiments) {
        const edgeWorkerExperiments = useEdgeWorkerExperiments()
        watch(() => edgeWorkerExperiments.value, () => {
          if (Object.keys(edgeWorkerExperiments.value).length)
            push('launchDarkly.onActiveEdgeWorkerExperiences')
        }, { immediate: true })
      }

      const experiments = useExperiments()
      watch(experiments, () => {
        if (Object.keys(experiments).length)
          push('launchDarkly.onActiveExperiences')
      }, { immediate: true })
    })

    return {
      provide: {
        gtm: {
          flushQueue,
          push,
          pushEventObj,
          queueEvent,
          disableAppEvents: [
            'c-slug',
            'cart',
            'checkout',
            'index',
            'order-confirmation',
            'p-slug',
            'search',
            'agent-results',
            'slug',
            'help-slug',
            'help-slug-child_slug',
            'help-slug-slug',
            'help-slug-slug-child_slug'
          ]
        }
      }
    }
  }
})
